import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_ENDPOINTS } from '../utils/config';

interface Company {
  _id: string;
  name: string;
  email: string;
  domain?: string;
  logo_url?: string;
  website?: string;
}

interface User {
  _id: string;
  email: string;
  name: string;
  role: 'user' | 'admin' | 'procurement' | 'useradmin';
  is_admin?: boolean;
  department?: string;
  phone?: string;
  company_id?: string;
  company?: Company;
  permissions?: {
    open_rfq_request?: boolean;
    edit_supplier?: boolean;
    edit_product?: boolean;
    approve_ability?: boolean;
    see_all_requests?: boolean;
    user_rights?: boolean;
    edit_address?: boolean;
    control_ability?: boolean;
    rfq_operations?: boolean;
  };
};


interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  loginWithToken: (accessToken: string) => Promise<{ success: boolean; error?: string; user?: User }>;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is logged in on app start
  useEffect(() => {
    const savedToken = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('auth_user');

    if (savedToken && savedUser) {
      try {
        // Validate token by making a test request
        fetch(API_ENDPOINTS.USERS + '/me', {
          headers: {
            'Authorization': `Bearer ${savedToken}`,
            'Content-Type': 'application/json',
          },
        })
          .then(response => {
            if (response.ok) {
              // Token is valid, restore user data
              setToken(savedToken);
              setUser(JSON.parse(savedUser));
            } else {
              // Token is invalid, clear storage
              console.log('Token expired or invalid, clearing storage');
              localStorage.removeItem('auth_token');
              localStorage.removeItem('auth_user');
              localStorage.removeItem('user_type');
            }
            setIsLoading(false);
          })
          .catch(error => {
            console.error('Error validating token:', error);
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_user');
            localStorage.removeItem('user_type');
            setIsLoading(false);
          });
      } catch (error) {
        console.error('Error parsing saved user data:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('user_type');
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchCurrentUser = async (
    accessToken: string
  ): Promise<{ success: boolean; user?: User; error?: string }> => {
    try {
      console.log('🔍 fetchCurrentUser: Calling /me endpoint...');
      const meResponse = await fetch(API_ENDPOINTS.USERS + '/me', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('🔍 fetchCurrentUser: Response status:', meResponse.status, meResponse.statusText);

      if (!meResponse.ok) {
        const errorPayload = await meResponse.json().catch(() => ({}));
        const message =
          (errorPayload && (errorPayload.error || errorPayload.message)) ||
          'Failed to get user data';
        console.error('❌ fetchCurrentUser: Response not OK:', message);
        return { success: false, error: message };
      }

      const meData = await meResponse.json();
      console.log('🔍 fetchCurrentUser: Response data:', {
        success: meData.success,
        hasData: !!meData.data,
        error: meData.error
      });

      if (meData.success && meData.data) {
        return { success: true, user: meData.data };
      }

      console.error('❌ fetchCurrentUser: Invalid response structure:', meData);
      return { success: false, error: meData.error || 'Failed to get user data' };
    } catch (error) {
      console.error('❌ fetchCurrentUser: Exception:', error);
      return { success: false, error: 'Failed to get user data' };
    }
  };

  const applyAuthenticatedSession = (accessToken: string, userData: User) => {
    setToken(accessToken);
    setUser(userData);
    localStorage.setItem('auth_token', accessToken);
    localStorage.setItem('auth_user', JSON.stringify(userData));
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(API_ENDPOINTS.USERS + '/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Login failed');
      }

      const data = await response.json();

      if (data.success) {
        const { access_token } = data.data;

        if (!access_token) {
          console.error('Missing access_token in response');
          return { success: false, error: 'Missing access token' };
        }

        const currentUserResult = await fetchCurrentUser(access_token);
        if (currentUserResult.success && currentUserResult.user) {
          applyAuthenticatedSession(access_token, currentUserResult.user);
          return { success: true };
        }

        console.error('Failed to get user data from /me endpoint');
        return { success: false, error: currentUserResult.error || 'Failed to get user data' };
      } else {
        throw new Error(data.error || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Login failed' };
    }
  };

  const loginWithToken = async (accessToken: string): Promise<{ success: boolean; error?: string; user?: User }> => {
    console.log('🔍 loginWithToken called with token length:', accessToken?.length);
    try {
      const currentUserResult = await fetchCurrentUser(accessToken);
      console.log('🔍 fetchCurrentUser result:', {
        success: currentUserResult.success,
        hasUser: !!currentUserResult.user,
        error: currentUserResult.error
      });

      if (currentUserResult.success && currentUserResult.user) {
        applyAuthenticatedSession(accessToken, currentUserResult.user);
        console.log('✅ SSO login successful, user authenticated');
        return { success: true, user: currentUserResult.user };
      }

      console.error('❌ SSO login failed:', currentUserResult.error);
      return {
        success: false,
        error: currentUserResult.error || 'Failed to complete SSO login'
      };
    } catch (error) {
      console.error('❌ loginWithToken exception:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to complete SSO login'
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    loginWithToken,
    logout,
    isLoading,
    isAuthenticated: !!user && !!token,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 